import { Product } from '@la/types';
import { formatAsUSD } from '@la/utilities';
import { Fee } from '../FeesCard/FeesCard';
import { FeeRange, RegistrationFee } from '../TournamentDetails.types';

export function convertRegFeesToNumbers(fees: Fee[]): RegistrationFee[] {
  return fees.map((fee: Fee) => {
    return {
      label: fee.label,
      min: Number(fee.min),
      max: Number(fee.max),
    };
  });
}

export function compileRegistrationFeesData(
  registrationFees: RegistrationFee[],
  requiredPlayerAddOns: Product[],
  requiredStaffAddOns: Product[],
  showTeamPlayerFees?: boolean,
  showTeamStaffFees?: boolean
) {
  return registrationFees.map((fee) => {
    // Filter out hidden team fees
    if (!showTeamPlayerFees && fee.label === 'Team Player') {
      return null;
    }
    if (!showTeamStaffFees && fee.label === 'Team Staff Member') {
      return null;
    }

    // Determine registration type and add-ons based on label
    let registrationType: 'player' | 'staff' | undefined;
    let addOns: Product[] | undefined;

    if (fee.label.match(/player/i)) {
      registrationType = 'player';
      addOns = requiredPlayerAddOns;
    } else if (fee.label.match(/staff/i)) {
      registrationType = 'staff';
      addOns = requiredStaffAddOns;
    } else {
      registrationType = undefined;
      addOns = undefined;
    }

    return {
      registrationType,
      registrationTypeLabel: fee.label,
      ...generateCardRowData(fee, addOns),
    };
  });
}

export function generateCardRowData(
  regFee: RegistrationFee,
  requiredAddOns?: Product[]
) {
  const registrationTypeRow = getRegTypeRangeDisplay(regFee);
  const addOnData =
    requiredAddOns?.length && requiredAddOns.length > 0
      ? getAddOnFeeData(requiredAddOns)
      : null;
  const addOnRow = getAddOnRangeDisplay(addOnData);
  const totalCostRow = getTotalCostRangeDisplay(regFee, addOnData);

  return { addOnRow, registrationTypeRow, totalCostRow };
}

export function getRegTypeRangeDisplay(regFee: {
  min: number;
  max: number;
}): string {
  if (regFee.min === 0 && regFee.max === 0) {
    return 'Free';
  }
  if (regFee.min === 0) {
    return `Free - ${formatAsUSD(regFee.max)}`;
  }
  if (regFee.max === 0 || regFee.min === regFee.max) {
    return `${formatAsUSD(regFee.min)}`;
  }

  return `${formatAsUSD(regFee.min)} - ${formatAsUSD(regFee.max)}`;
}

export function getAddOnRangeDisplay(
  addOnData: {
    maxFee: number;
    minFee: number;
    salesTaxRequired: boolean;
  } | null
): string | null {
  if (!addOnData) {
    return null;
  }

  const { minFee, maxFee, salesTaxRequired } = addOnData;

  const display = getAddOnPriceRange({ maxFee, minFee });

  return salesTaxRequired ? display + ' +\u00A0tax' : display;
}

export function getAddOnPriceRange({ maxFee, minFee }: FeeRange) {
  if (minFee === 0 && maxFee === 0) {
    return 'Included';
  } else if (minFee === 0) {
    return `Included - ${formatAsUSD(maxFee)}`;
  } else if (maxFee === 0 || minFee === maxFee) {
    return formatAsUSD(minFee);
  } else {
    return `${formatAsUSD(minFee)} - ${formatAsUSD(maxFee)}`;
  }
}

export function getTotalCostRangeDisplay(
  regFee: RegistrationFee,
  addOnData: {
    minFee: number;
    maxFee: number;
    salesTaxRequired: boolean;
  } | null
) {
  const regFeeMin = regFee.min;
  const regFeeMax = regFee.max;
  const addOnMin = addOnData ? addOnData?.minFee : 0;
  const addOnMax = addOnData ? addOnData?.maxFee : 0;
  const addTax = addOnData ? addOnData?.salesTaxRequired : false;
  const totalCostMin = regFeeMin + addOnMin;
  const totalCostMax =
    Math.max(regFeeMin, regFeeMax) + Math.max(addOnMin, addOnMax);

  if (totalCostMin === totalCostMax) {
    const display = `${formatAsUSD(totalCostMin)}`;
    return addTax ? display + ' + tax' : display;
  }

  const display = `${formatAsUSD(totalCostMin)} - ${formatAsUSD(totalCostMax)}`;
  return addTax ? display + ' + tax' : display;
}

export function getAddOnFeeData(requiredAddOns: Product[]) {
  const addOnData: FeeRange[] = extractAddOnFeeData(requiredAddOns);
  const { minFee, maxFee } = addOnData.reduce(sumFeeRanges, {
    minFee: 0,
    maxFee: 0,
  });
  const salesTaxRequired = requiredAddOns.some(
    (addOn) => addOn.salesTaxRequired
  );
  return {
    minFee,
    maxFee,
    salesTaxRequired,
  };
}

export function extractAddOnFeeData(requiredAddOns: Product[]) {
  const addOnData: FeeRange[] = [];
  requiredAddOns.forEach((addOn) => {
    addOnData.push(extractFeeRange(addOn));
  });

  return addOnData;
}

export function extractFeeRange(addOn: Product) {
  const minFee = addOn.price;

  // Find the highest price in pricingConfigurations
  const maxAdditionalPrice =
    addOn.pricingConfigurations?.reduce(
      (max, config) => Math.max(max, config.price ?? 0),
      0
    ) ?? 0;

  const maxFee = minFee + maxAdditionalPrice;
  return { minFee, maxFee };
}

function sumFeeRanges(acc: FeeRange, current: FeeRange): FeeRange {
  acc.minFee += current.minFee;
  acc.maxFee += current.maxFee;
  return acc;
}
